import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form } from 'reactstrap';
import { post } from '../../../components/hooks/apiFetch';
import { TextDropZone } from '../../DropZones/TextDropZone';
import { useUniversalSummarizeResumeApi } from './UniversalSummarizeResumeApi';
import { SubmitButton } from '../shared/SubmitButton';
import { AdvancedPromptInput } from '../shared/AdvancedPromptInput';
import { AI_UTILITIES } from '../../../constants/aiUtilities';
import { useNotification } from '../../../components/index';
import { useMountedRef } from '../../../components/hooks/useMountedRef';
import { useRepeatProcessingLoader } from '../shared/hooks/useRepeatProcessingLoader';
import { StartTimePicker } from '../shared/StartTimePicker';
import { CommentInput } from '../shared/CommentInput';
import { useUserPreferences } from '../shared/hooks/useUserPreferences';
import { DEFAULT_OUTPUT_FORMAT, OUTPUT_FORMATS } from '../shared/constants/outputFormats';

export const UniversalSummarizeResumeLoader = () => {
    const history = useHistory();
    const location = useLocation();
    const { getPreferences, savePreferences } = useUserPreferences();

    const [files, setFiles] = useState([]);
    const [comment, setComment] = useState(location.state?.comment || '');
    const [startTime, setStartTime] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [selectedSource, setSelectedSource] = useState(location.state?.promptInfo || null);
    const [promptText, setPromptText] = useState(location.state?.promptInfo?.prompt || '');
    const [isSavingPrompt, setIsSavingPrompt] = useState(false);
    const [defaultOutputFormat, setDefaultOutputFormat] = useState(DEFAULT_OUTPUT_FORMAT);
    const [defaultTemplate, setDefaultTemplate] = useState(null);

    // Режим повторной обработки
    const isRepeatMode = useRef(location.state?.isRepeatProcessing || false);
    const userInteracted = useRef(false);

    const { submitUniversalSummarizeResumeRequest } = useUniversalSummarizeResumeApi();
    const {
        showSuccess,
        showError,
        showWarning
    } = useNotification();
    const isMounted = useMountedRef();

    useEffect(() => {
        if (isRepeatMode.current) {
            return;
        }
        const loadPreferences = async () => {
            try {
                const prefs = await getPreferences(AI_UTILITIES.UNIVERSAL_SUMMARIZE.id);
                // Валидация выходного формата
                const validFormats = OUTPUT_FORMATS.map(f => f.value);
                if (prefs.defaultOutputFormat && validFormats.includes(prefs.defaultOutputFormat)) {
                    setDefaultOutputFormat(prefs.defaultOutputFormat);
                } else {
                    setDefaultOutputFormat(DEFAULT_OUTPUT_FORMAT);
                }
                // Шаблон пока не проверяем (загрузится позже в DropZoneBase)
                if (prefs.defaultTemplate) {
                    setDefaultTemplate(prefs.defaultTemplate);
                } else {
                    setDefaultTemplate(null);
                }
            } catch (error) {
                // Если предпочтений нет или ошибка – используем дефолты
                console.warn('Failed to load preferences', error);
            }
        };
        loadPreferences();
    }, []);

    useRepeatProcessingLoader({
        locationState: location.state,
        setFiles,
        setIsLoading,
        setIsInitializing,
        showError,
        isMounted,
        onLoaded: (state) => {
            setComment(state.comment || '');
        },
    });

    const saveCurrentPreferences = async () => {
        if (isRepeatMode.current && !userInteracted.current) return;
        try {
            await savePreferences(AI_UTILITIES.UNIVERSAL_SUMMARIZE.id, {
                defaultOutputFormat,
                defaultTemplate,
            });
        } catch (error) {
            console.error('Failed to save preferences', error);
        }
    };

    const handlePromptSave = async (source, text, ratingValue, status, model, description) => {
        setIsSavingPrompt(true);
        const currentDate = new Date().toISOString();
        const formData = new FormData();

        const getNameFromSource = (src) => {
            if (typeof src === "string") return src;

            if (src?.name) return src.name;

            // Собираем строку из числовых ключей
            if (src && typeof src === 'object') {
                // Получаем все числовые ключи, сортируем и объединяем
                const str = Object.keys(src)
                    .filter(key => !isNaN(key) && src[key] !== undefined)
                    .sort((a, b) => a - b)
                    .map(key => src[key])
                    .join('');

                if (str) return str;
            }

            return "Новый промпт";
        };

        const promptDTO = {
            Guid: source?.guid ?? "",
            Name: getNameFromSource(source),
            Prompt: text,
            CreationAt: source?.creationAt ?? currentDate,
            UpdateAt: currentDate,
            CreatorLogin: source?.creatorLogin ?? "",
            LastRedactorLogin: source?.lastRedactorLogin ?? "",
            FileName: source?.fileName ?? "",
            Rating: source?.rating ?? ratingValue,
            Status: status || 0,
            Model: model || 0,
            Description: description || '',
            FilePath: source?.filePath ?? "",
            IsDeleted: source?.isDeleted ?? false
        };

        formData.append('PromptDTO.Guid', promptDTO.Guid);
        formData.append('PromptDTO.Name', promptDTO.Name);
        formData.append('PromptDTO.Prompt', promptDTO.Prompt);
        formData.append('PromptDTO.CreationAt', promptDTO.CreationAt);
        formData.append('PromptDTO.UpdateAt', promptDTO.UpdateAt);
        formData.append('PromptDTO.CreatorLogin', promptDTO.CreatorLogin);
        formData.append('PromptDTO.LastRedactorLogin', promptDTO.LastRedactorLogin);
        formData.append('PromptDTO.Rating', promptDTO.Rating.toString().replace('.', ','));
        formData.append('PromptDTO.Status', promptDTO.Status);
        formData.append('PromptDTO.Model', promptDTO.Model);
        formData.append('PromptDTO.Description', promptDTO.Description);
        formData.append('PromptDTO.IsDeleted', promptDTO.IsDeleted);
        formData.append('PromptDTO.FileName', promptDTO.FileName);
        formData.append('PromptDTO.FilePath', promptDTO.FilePath);

        formData.append('Version', source?.version ?? '');

        try {
            if (promptDTO.Name === null || promptDTO.Name === undefined || promptDTO.Name === '')
            {
                throw new Error('Имя промпта не может быть пустым!')
            }

            await post('/api/Prompt/AddOrUpdatePrompt', formData);
            showSuccess('Промпт успешно сохранен', 'success');
        } catch (error) {
            showError(error.message);
        } finally {
            setIsSavingPrompt(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (files.length === 0 || !selectedSource) {
            showWarning("Пожалуйста, выберите файлы или настройте prompt");
            return;
        }

        try {
            saveCurrentPreferences();
        }
        catch (error) {
            console.error(error);
        }

        setIsLoading(true);

        const startTimeString = startTime ? startTime.toISOString() : '';
        try {
            const { id, identifier } = await submitUniversalSummarizeResumeRequest(
                files
                , typeof selectedSource === "string" ? selectedSource : selectedSource?.name
                , promptText
                , selectedSource?.guid
                , comment
                , selectedSource?.version
                , startTimeString
            );

            if (isMounted.current) {
                history.push({
                    pathname: `${AI_UTILITIES.UNIVERSAL_SUMMARIZE.path}/${identifier}`,
                    state: {
                        id,
                        fileNames: files.map(f => f.file.name),
                        promptUsed: !!selectedSource,
                        comment: comment
                    }
                });
            }
        } catch (error) {
            showError(error.message);
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    };

    const handleFilesChange = (newFiles) => {
        if (!isLoading && !isInitializing) {
            setFiles(newFiles);
        }
    };

    const handleFormatChange = (newFormat) => {
        setDefaultOutputFormat(newFormat);
        userInteracted.current = true;
        isRepeatMode.current = false;
    };

    const handleTemplateChange = (newTemplate) => {
        setDefaultTemplate(newTemplate);
        userInteracted.current = true;
        isRepeatMode.current = false;
    };

    return (
        <Form onSubmit={handleSubmit} className="speakers-form-container">
            <div style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid var(--DT1520-Border)', borderRadius: '8px' }}>
                <AdvancedPromptInput
                    isLoading={isSavingPrompt}
                    onSourceChange={setSelectedSource}
                    onTextChange={setPromptText}
                    selectedSource={selectedSource}
                    textValue={promptText}
                    onSave={handlePromptSave}
                    disabled={isLoading || isInitializing}
                />

            </div>

            <CommentInput
                id="depersonalizeComment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={isLoading || isInitializing}
            />

            <StartTimePicker
                value={startTime}
                onChange={setStartTime}
                disabled={isLoading || isInitializing}
            />

            <TextDropZone
                files={files}
                isLoading={isLoading || isInitializing}
                onFilesChange={handleFilesChange}
                expectedContent="Текстовые файлы под целевую обработку промптом"
                showFormatSelector={true}
                maxFiles={2}
                showTemplateSelector={true}
                defaultOutputFormat={defaultOutputFormat}
                defaultTemplate={defaultTemplate}
                onFormatChange={handleFormatChange}
                onTemplateChange={handleTemplateChange}
            />

            <SubmitButton
                isLoading={isLoading}
                text="Отправить на обработку ИИ"
                disabled={isLoading || isInitializing || files.length === 0}
            />
        </Form>
    );
};